import { useGovmapGroups } from '@issuemanager/client/features/govmap-groups';
import { JSX, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { truncateSearchText } from '../../utils';
import { SearchBox } from '../inputs';
import { useInputRef } from '../inputs/search-box/hooks/use-input-ref';
import { TreeViewSelector, TreeViewSelectorProps } from '../tree-view-selector';
import { mapDataToTreeViewNode } from './utils';

type Props = Pick<TreeViewSelectorProps, 'value' | 'onChange'> & {
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  isVisible?: boolean;
};

export function GovmapGroupsSelector({
  value = [],
  onChange,
  label,
  required,
  error,
  helperText,
  readOnly,
  isVisible = true,
}: Props): JSX.Element | null {
  const [searchTerm, setSearchTerm] = useState('');
  const [ids, setIds] = useState<string[]>([]);
  const [pendingChanges, setPendingChanges] = useState<string[]>([]);
  const { inputRef, focusInput } = useInputRef(searchTerm);

  const { t } = useTranslation();

  const govmapGroups = useGovmapGroups({
    filter: {
      searchTerm,
      ids,
    },
    sort: {
      name: 'asc',
    },
  });

  const data = useMemo(
    () => mapDataToTreeViewNode(govmapGroups.data ?? []),
    [govmapGroups.data],
  );

  const handleClear = useCallback(() => {
    setSearchTerm('');
    focusInput();
  }, [focusInput]);

  const handleSearchTermChange = useCallback(
    async (term: string) => {
      setSearchTerm(truncateSearchText(term));
      setIds(pendingChanges);
    },
    [pendingChanges],
  );

  const handleChangePendingChanges = useCallback(
    (values: string[]) => {
      setPendingChanges(values);
    },
    [setPendingChanges],
  );

  if (!isVisible) return null;

  return (
    <TreeViewSelector
      value={value}
      onChange={onChange}
      pendingChanges={pendingChanges}
      onChangePendingChanges={handleChangePendingChanges}
      searchTerm={searchTerm}
      readOnly={readOnly}
      TextFieldProps={{
        label: label ?? '',
        required,
        helperText,
        error,
        placeholder: t('inputs.listBox.empty'),
        'data-testid': 'entities',
      }}
      data={data}
      noOptionsText={t('inputs.listBox.noResults')}
      isLoading={govmapGroups.isLoading}
      searchComponent={
        <SearchBox
          fullWidth
          placeholder={t('inputs.listBox.search')}
          onSearchTermChange={handleSearchTermChange}
          inputRef={inputRef}
          data-testid='search-box'
        />
      }
      onClear={handleClear}
      treeLeavesDataTestId='govmap-groups-options'
    />
  );
}
